import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  Button,
  useTheme,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BoltIcon from '@mui/icons-material/Bolt';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import HotelIcon from '@mui/icons-material/Hotel';
import dayjs from 'dayjs';

import PeakieWebLogo from '../../../../../assets/Peakie AI Web Logo.png';
import { StatCard, AnnotatableChart, MetricTargetBars } from '../../../../../shared/components/charts';
import { DashboardCard } from '../../../../../shared/components/DashboardCard';
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  GranularityToggle,
  DateRange,
  DashboardCardSkeleton,
  CohortGuard,
  WeatherAnomalyCallout,
} from '../../../../../shared/components';
import { DepartmentSelect } from '../../../../../shared/components/Filters/components';
import { useSessionQuery } from '../../../../../features/auth/hooks';
import { useInsightsChatbotConfig, useStandardInsights } from '../../../insights';
import type { InsightScope } from '../../../insights/api';
import { AiWellnessPanel } from '../../../insights/components';
import {
  useClientDashboardSettings,
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
} from '../../../../../shared/hooks';
import { useClientScope } from '../../../../../shared/contexts';
import { useTeamProgressStats, useTeamProgressTrends, useWellbeingDistribution } from '../hooks/useTeamProgress';
import { formatPercentValue, toMetricKey } from '../../../../../shared/utils';
import { buildFilterParams, firstSexValue, transformTrendsData, useResolvedGranularity } from '../../shared/utils';

interface TeamProgressBucket {
  key: string;
  label: string;
  dashboardKey: string;
}

const TEAM_PROGRESS_BUCKETS: TeamProgressBucket[] = [
  { key: 'biometrics', label: 'Biometric Trends', dashboardKey: 'biometrics_vitals' },
  { key: 'sleep', label: 'Sleep Trends', dashboardKey: 'sleep_analytics' },
  { key: 'recovery', label: 'Recovery Trends', dashboardKey: 'recovery' },
  { key: 'strain', label: 'Strain Trends', dashboardKey: 'strain_activity' },
];

const truncateSummary = (text: string, maxChars: number) => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxChars) return normalized;

  const hardCut = normalized.slice(0, maxChars).trim();
  const lastWordBoundary = hardCut.lastIndexOf(' ');
  const clipped = lastWordBoundary > Math.floor(maxChars * 0.7) ? hardCut.slice(0, lastWordBoundary).trim() : hardCut;

  return `${clipped}...`;
};

export const TeamProgress: React.FC = () => {
  const theme = useTheme();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { granularityMode, setGranularityMode, granularity } = useResolvedGranularity(dateRange);
  const [viewMode, setViewMode] = useState('team-progress');
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const { data: dashboardSettings } = useClientDashboardSettings();
  const isAiEnabled =
    dashboardSettings?.data?.find((setting) => setting.dashboard_key === 'team_progress')?.ai_enabled ?? true;
  const { setConfig: setChatbotConfig } = useInsightsChatbotConfig();
  const [biometricTrendView, setBiometricTrendView] = useState('heart-rate');
  const [sleepTrendView, setSleepTrendView] = useState('sleep-stages');
  const [recoveryTrendView, setRecoveryTrendView] = useState('recovery-score');
  const [strainTrendView, setStrainTrendView] = useState('strain');
  const [selectedBucket, setSelectedBucket] = useState<string>('all');

  // Determine which buckets are available for the current client based on dashboard settings.
  // If no settings are found (data is undefined/empty), fall back to showing all buckets.
  const availableBuckets = useMemo(() => {
    const settings = dashboardSettings?.data;
    if (!settings || settings.length === 0) {
      return TEAM_PROGRESS_BUCKETS;
    }
    const enabledKeys = new Set(settings.filter((s) => s.is_enabled !== false).map((s) => s.dashboard_key));
    const filtered = TEAM_PROGRESS_BUCKETS.filter((b) => enabledKeys.has(b.dashboardKey));
    // Fall back to all buckets if none match (e.g. client settings don't include these keys yet)
    return filtered.length > 0 ? filtered : TEAM_PROGRESS_BUCKETS;
  }, [dashboardSettings]);

  const handleApplyFilters = (filters: FilterState) => {
    setFilters(filters);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  };

  useEffect(() => {
    if (!isAiEnabled && viewMode === 'ai-powered-wellness') {
      setViewMode('team-progress');
    }
  }, [isAiEnabled, viewMode]);

  // Reset bucket selection if the current bucket is no longer available for this client.
  useEffect(() => {
    if (selectedBucket !== 'all' && !availableBuckets.some((b) => b.key === selectedBucket)) {
      setSelectedBucket('all');
    }
  }, [availableBuckets, selectedBucket]);

  const insightsDepartmentId = selectedDepartmentIds.length === 1 ? selectedDepartmentIds[0] : null;

  const filterParams = useMemo(() => {
    const baseParams = buildFilterParams(filters, dateRange, effectiveClientId, null, null, { isCentralAdmin });
    return {
      ...baseParams,
      department: selectedDepartmentIds.length > 0 ? selectedDepartmentIds : undefined,
    };
  }, [filters, dateRange, selectedDepartmentIds, effectiveClientId, isCentralAdmin]);

  // Client events: central admins and client admins may annotate; the popover
  // is read-only for everyone else. Only enabled for a single selected client.
  const { data: session } = useSessionQuery();
  const canEditEvents = Boolean(isCentralAdmin || session?.is_admin);
  const [startDate, endDate] = dateRange;
  const eventStartDate = startDate ? startDate.format('YYYY-MM-DD') : undefined;
  const eventEndDate = endDate ? endDate.format('YYYY-MM-DD') : undefined;

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    const getRange = (key: string) => ({
      min: typeof metric_min[key] === 'number' ? (metric_min[key] as number) : undefined,
      max: typeof metric_max[key] === 'number' ? (metric_max[key] as number) : undefined,
    });
    return {
      avg_heart_rate: getRange('avg_heart_rate'),
      resting_heart_rate: getRange('resting_heart_rate'),
      max_heart_rate: getRange('max_heart_rate'),
      hrv: getRange('hrv'),
      respiratory_rate: getRange('respiratory_rate'),
      spo2: getRange('spo2'),
      sleep_efficiency: getRange('sleep_efficiency'),
      sleep_debt: getRange('sleep_debt'),
      sleep_stress: getRange('sleep_stress'),
      recovery_score: getRange('recovery_score'),
      day_strain: getRange('day_strain'),
      calories_burn: getRange('calories_burn'),
      hours_of_sleep: getRange('hours_of_sleep'),
    };
  }, [overallRangeQuery.data]);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  const statsQuery = useTeamProgressStats(filterParams);
  const trendsQuery = useTeamProgressTrends({
    ...filterParams,
    granularity,
  });
  const distributionQuery = useWellbeingDistribution(filterParams);

  // A single selected team → team (department) insights; no single team →
  // organisation-wide insights across all teams (no need to pick one).
  const standardInsightsParams = useMemo(() => {
    const start = dateRange[0] ?? dayjs().startOf('month');
    const end = dateRange[1] ?? dayjs().endOf('month');
    return {
      scope_type: insightsDepartmentId ? ('department' as const) : ('org' as const),
      scope_id: insightsDepartmentId ?? undefined,
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD'),
      client_id: effectiveClientId || undefined,
      department: selectedDepartmentIds.length > 0 ? selectedDepartmentIds : undefined,
      age_range: filterParams.age_range,
      sex: firstSexValue(filterParams.sex),
      shift_pattern: filterParams.shift_pattern,
      job_role: filterParams.job_role,
    };
  }, [
    dateRange,
    insightsDepartmentId,
    effectiveClientId,
    selectedDepartmentIds,
    filterParams.age_range,
    filterParams.sex,
    filterParams.shift_pattern,
    filterParams.job_role,
  ]);

  const {
    data: standardInsightsData,
    isLoading: standardInsightsLoading,
    error: standardInsightsError,
  } = useStandardInsights(standardInsightsParams, {
    enabled: isAiEnabled,
  });

  const standardInsights = standardInsightsData?.data;

  useEffect(() => {
    // Scope the assistant to the single selected team, else the whole org.
    const scope: InsightScope = insightsDepartmentId
      ? { type: 'department', id: insightsDepartmentId }
      : { type: 'org' };
    setChatbotConfig({
      title: 'Peakie AI',
      initialPrompt: 'What stands out this week and what should we do next?',
      buildRequest: (question) => ({
        scope,
        question,
        start_date: standardInsightsParams.start_date,
        end_date: standardInsightsParams.end_date,
        client_id: standardInsightsParams.client_id,
        k: 8,
      }),
    });
  }, [
    insightsDepartmentId,
    setChatbotConfig,
    standardInsightsParams.end_date,
    standardInsightsParams.start_date,
    standardInsightsParams.client_id,
  ]);

  const trendsData = useMemo(() => {
    return transformTrendsData(trendsQuery.data, [
      'day_strain',
      'avg_heart_rate',
      'max_heart_rate',
      'respiratory_rate',
      'spo2',
      'sleep_efficiency',
      'sleep_debt',
      'sleep_stress',
      'hrv',
      'resting_heart_rate',
      'recovery_score',
      'sleep_stage_awake',
      'sleep_stage_light',
      'sleep_stage_rem',
      'sleep_stage_deep',
      'hours_of_sleep',
      'calories_burn',
    ]);
  }, [trendsQuery.data]);

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      {
        metric: stats.recovery_score,
        icon: FavoriteIcon,
      },
      {
        metric: stats.sleep_performance,
        icon: BedtimeIcon,
      },
      {
        metric: stats.nap_duration,
        icon: HotelIcon,
      },
      {
        metric: stats.hrv,
        icon: MonitorHeartIcon,
      },
      {
        metric: stats.day_strain,
        icon: BoltIcon,
      },
      {
        metric: stats.respiratory_rate,
        icon: AirIcon,
      },
      {
        metric: stats.spo2,
        icon: WaterDropIcon,
      },
      {
        metric: stats.calories_burn,
        icon: WhatshotIcon,
      },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const wellbeingDimensionsData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      metric: item.label,
      currentScore: item.value,
      optimalTarget: 100,
    }));
  }, [distributionQuery.data]);

  const isLoading = statsQuery.isLoading || trendsQuery.isLoading || distributionQuery.isLoading;

  const hasError = statsQuery.isError || trendsQuery.isError || distributionQuery.isError;

  const renderBiometricTrendChart = () => {
    if (biometricTrendView === 'heart-rate') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'avg_heart_rate',
              label: 'Avg HR',
              color: theme.palette.info.main,
              area: true,
            },
            {
              dataKey: 'resting_heart_rate',
              label: 'Resting HR',
              color: theme.palette.success.main,
              area: true,
            },
            {
              dataKey: 'max_heart_rate',
              label: 'Max HR',
              color: theme.palette.error.main,
              dashed: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          showMinMaxLines
          referenceMin={overallMinMax.avg_heart_rate.min}
          referenceMax={overallMinMax.avg_heart_rate.max}
        />
      );
    }

    if (biometricTrendView === 'heart-rate-variability') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'hrv',
              label: 'Heart Rate Variability',
              color: theme.palette.success.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          showMinMaxLines
          referenceMin={overallMinMax.hrv.min}
          referenceMax={overallMinMax.hrv.max}
        />
      );
    }

    if (biometricTrendView === 'resting-hr') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'resting_heart_rate',
              label: 'Resting HR',
              color: theme.palette.warning.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          showMinMaxLines
          referenceMin={overallMinMax.resting_heart_rate.min}
          referenceMax={overallMinMax.resting_heart_rate.max}
        />
      );
    }

    if (biometricTrendView === 'respiratory-rate') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'respiratory_rate',
              label: 'Respiratory Rate',
              color: theme.palette.warning.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={25}
          showMinMaxLines
          referenceMin={overallMinMax.respiratory_rate.min}
          referenceMax={overallMinMax.respiratory_rate.max}
        />
      );
    }

    return (
      <AnnotatableChart
        clientId={effectiveClientId}
        startDate={eventStartDate}
        endDate={eventEndDate}
        weatherFilters={filterParams}
        canEdit={canEditEvents}
        data={trendsData}
        xAxisKey='label'
        series={[
          {
            dataKey: 'spo2',
            label: 'Blood Oxygen (SpO2)',
            color: theme.palette.success.main,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        yAxisMax={100}
        showMinMaxLines
        referenceMin={overallMinMax.spo2.min}
        referenceMax={overallMinMax.spo2.max}
      />
    );
  };

  const renderSleepTrendChart = () => {
    if (sleepTrendView === 'sleep-stages') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'sleep_stage_deep',
              label: 'Slow Wave (Deep)',
              color: theme.palette.success.main,
              area: true,
              stack: 'sleep',
            },
            {
              dataKey: 'sleep_stage_rem',
              label: 'REM',
              color: theme.palette.info.dark,
              area: true,
              stack: 'sleep',
            },
            {
              dataKey: 'sleep_stage_light',
              label: 'Light Sleep',
              color: theme.palette.info.light,
              area: true,
              stack: 'sleep',
            },
            {
              dataKey: 'sleep_stage_awake',
              label: 'Awake',
              color: theme.palette.error.main,
              area: true,
              stack: 'sleep',
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={10}
          showMinMaxLines
        />
      );
    }

    if (sleepTrendView === 'total-sleep') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'hours_of_sleep',
              label: 'Total Sleep (hrs)',
              color: theme.palette.primary.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={12}
          showMinMaxLines
          referenceMin={overallMinMax.hours_of_sleep.min}
          referenceMax={overallMinMax.hours_of_sleep.max}
        />
      );
    }

    const metricKey =
      sleepTrendView === 'efficiency'
        ? 'sleep_efficiency'
        : sleepTrendView === 'sleep-debt'
          ? 'sleep_debt'
          : 'sleep_stress';

    const metricLabel =
      sleepTrendView === 'efficiency'
        ? 'Sleep Efficiency'
        : sleepTrendView === 'sleep-debt'
          ? 'Sleep Debt'
          : 'Sleep Stress';

    const metricColor = sleepTrendView === 'sleep-stress' ? theme.palette.error.main : theme.palette.success.main;

    const metricRange = overallMinMax[metricKey as keyof typeof overallMinMax];

    return (
      <AnnotatableChart
        clientId={effectiveClientId}
        startDate={eventStartDate}
        endDate={eventEndDate}
        weatherFilters={filterParams}
        canEdit={canEditEvents}
        data={trendsData}
        xAxisKey='label'
        series={[
          {
            dataKey: metricKey,
            label: metricLabel,
            color: metricColor,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        showMinMaxLines
        referenceMin={metricRange?.min}
        referenceMax={metricRange?.max}
      />
    );
  };

  const renderRecoveryTrendChart = () => {
    if (recoveryTrendView === 'recovery-score') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'recovery_score',
              label: 'Recovery Score',
              color: theme.palette.success.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={100}
          showMinMaxLines
          referenceMin={overallMinMax.recovery_score.min}
          referenceMax={overallMinMax.recovery_score.max}
        />
      );
    }

    return (
      <AnnotatableChart
        clientId={effectiveClientId}
        startDate={eventStartDate}
        endDate={eventEndDate}
        weatherFilters={filterParams}
        canEdit={canEditEvents}
        data={trendsData}
        xAxisKey='label'
        series={[
          {
            dataKey: 'resting_heart_rate',
            label: 'Resting Heart Rate',
            color: theme.palette.warning.main,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        showMinMaxLines
        referenceMin={overallMinMax.resting_heart_rate.min}
        referenceMax={overallMinMax.resting_heart_rate.max}
      />
    );
  };

  const renderStrainTrendChart = () => {
    if (strainTrendView === 'strain') {
      return (
        <AnnotatableChart
          clientId={effectiveClientId}
          startDate={eventStartDate}
          endDate={eventEndDate}
          weatherFilters={filterParams}
          canEdit={canEditEvents}
          data={trendsData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'day_strain',
              label: 'Day Strain',
              color: theme.palette.warning.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={21}
          showMinMaxLines
          referenceMin={overallMinMax.day_strain.min}
          referenceMax={overallMinMax.day_strain.max}
        />
      );
    }

    return (
      <AnnotatableChart
        clientId={effectiveClientId}
        startDate={eventStartDate}
        endDate={eventEndDate}
        weatherFilters={filterParams}
        canEdit={canEditEvents}
        data={trendsData}
        xAxisKey='label'
        series={[
          {
            dataKey: 'calories_burn',
            label: 'Calorie Expenditure (kcal)',
            color: theme.palette.error.light,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        showMinMaxLines
        referenceMin={overallMinMax.calories_burn.min}
        referenceMax={overallMinMax.calories_burn.max}
      />
    );
  };

  const getChipColor = (ragColor: string | undefined): any => {
    switch (ragColor) {
      case 'green':
        return 'success';
      case 'amber':
        return 'warning';
      case 'red':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Box sx={{ minWidth: 320 }}>
              <DepartmentSelect
                value={selectedDepartmentIds}
                onChange={setSelectedDepartmentIds}
                label='Teams (All if none selected)'
              />
            </Box>
            {availableBuckets.length > 1 && (
              <FormControl size='small' sx={{ minWidth: 180 }}>
                <InputLabel id='team-progress-bucket-label'>Section</InputLabel>
                <Select
                  labelId='team-progress-bucket-label'
                  value={selectedBucket}
                  label='Section'
                  onChange={(e) => setSelectedBucket(e.target.value as string)}
                >
                  <MenuItem value='all'>All Sections</MenuItem>
                  {availableBuckets.map((bucket) => (
                    <MenuItem key={bucket.key} value={bucket.key}>
                      {bucket.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <GranularityToggle value={granularityMode} onChange={setGranularityMode} />
            <DurationSelector value={dateRange} onChange={handleDateRangeChange} buttonVariant='outlined' />
            <Button
              variant='text'
              size='medium'
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
            </Button>
          </Box>
          <Stack direction='row' spacing={2} alignItems='center'>
            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size='small'>
              <ToggleButton value='team-progress'>Team Progress</ToggleButton>
              {isAiEnabled ? <ToggleButton value='ai-powered-wellness'>AI-Powered Wellness</ToggleButton> : null}
            </ToggleButtonGroup>
            <Box component='img' src={PeakieWebLogo} alt='Peakie AI' sx={{ height: 32, width: 'auto' }} />
          </Stack>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filters}
          hideDepartmentSelect={true}
          dateRange={dateRange}
        />

        {selectedDepartmentIds.length === 0 && (
          <Alert severity='info'>Showing data for all teams. Select one or more teams above to filter.</Alert>
        )}

        {viewMode === 'team-progress' && (
          <Stack spacing={3}>
            {hasError && <Alert severity='error'>Failed to load team progress data. Please try again later.</Alert>}

            <WeatherAnomalyCallout filters={filterParams} />

            <Grid container spacing={3}>
              {isLoading && statCards.length === 0 ? (
                <>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                      <DashboardCardSkeleton />
                    </Grid>
                  ))}
                </>
              ) : (
                statCards.map((stat, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                    <StatCard metric={stat.metric} icon={stat.icon} />
                  </Grid>
                ))
              )}
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <DashboardCard title='Pace of Aging' subtitle=''>
                  <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                    {isLoading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 300,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : statsQuery.data?.data ? (
                      <>
                        {statsQuery.data.data.pace_of_aging ? (
                          <Box sx={{ mb: 3 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <HourglassEmptyIcon
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontSize: 28,
                                }}
                              />
                              <Typography
                                variant='h4'
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '2rem',
                                }}
                              >
                                {statsQuery.data.data.pace_of_aging.value}x
                              </Typography>
                              {(() => {
                                const paceChange = statsQuery.data.data.pace_of_aging.change?.trim() || '';
                                const isNegative = paceChange.startsWith('-');
                                const isPositive = paceChange.startsWith('+');
                                const paceTrendColor = isNegative
                                  ? theme.palette.error.main
                                  : isPositive
                                    ? theme.palette.success.main
                                    : theme.palette.text.secondary;
                                const PaceTrendIcon = isNegative
                                  ? TrendingDownIcon
                                  : isPositive
                                    ? TrendingUpIcon
                                    : TrendingFlatIcon;

                                return (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.25,
                                      color: paceTrendColor,
                                    }}
                                  >
                                    <Typography
                                      component='span'
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {paceChange}
                                    </Typography>
                                    <PaceTrendIcon
                                      sx={{
                                        fontSize: '0.875rem',
                                        color: paceTrendColor,
                                      }}
                                    />
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Box>
                        ) : (
                          <Alert severity='info' sx={{ mb: 2 }}>
                            Pace of Aging score unavailable
                          </Alert>
                        )}
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <AirIcon fontSize='medium' sx={{ color: theme.palette.primary.main }} />
                            <Typography variant='body1' sx={{ flex: 1 }}>
                              Respiration
                            </Typography>
                            <Chip
                              label={statsQuery.data.data.respiratory_rate?.rag_label || 'N/A'}
                              size='small'
                              color={getChipColor(statsQuery.data.data.respiratory_rate?.rag_color)}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <WaterDropIcon fontSize='medium' sx={{ color: theme.palette.primary.main }} />
                            <Typography variant='body1' sx={{ flex: 1 }}>
                              SpO2
                            </Typography>
                            <Chip
                              label={statsQuery.data.data.spo2?.rag_label || 'N/A'}
                              size='small'
                              color={getChipColor(statsQuery.data.data.spo2?.rag_color)}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <FavoriteIcon fontSize='medium' sx={{ color: theme.palette.primary.main }} />
                            <Typography variant='body1' sx={{ flex: 1 }}>
                              Resting HR
                            </Typography>
                            <Chip
                              label={statsQuery.data.data.resting_heart_rate?.rag_label || 'N/A'}
                              size='small'
                              color={getChipColor(statsQuery.data.data.resting_heart_rate?.rag_color)}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <MonitorHeartIcon fontSize='medium' sx={{ color: theme.palette.primary.main }} />
                            <Typography variant='body1' sx={{ flex: 1 }}>
                              HRV
                            </Typography>
                            <Chip
                              label={statsQuery.data.data.hrv?.rag_label || 'N/A'}
                              size='small'
                              color={getChipColor(statsQuery.data.data.hrv?.rag_color)}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <ThermostatIcon fontSize='medium' sx={{ color: theme.palette.primary.main }} />
                            <Typography variant='body1' sx={{ flex: 1 }}>
                              Temperature
                            </Typography>
                            <Chip
                              label={statsQuery.data.data.temperature?.rag_label || 'N/A'}
                              size='small'
                              color={getChipColor(statsQuery.data.data.temperature?.rag_color)}
                            />
                          </Box>
                        </Stack>
                      </>
                    ) : (
                      <Alert severity='info'>No Pace of Aging data</Alert>
                    )}
                  </CohortGuard>
                </DashboardCard>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <DashboardCard
                  title='Wellbeing Dimensions Overview'
                  subtitle='Health assessment across all key metrics.'
                >
                  <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                    {isLoading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 250,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : wellbeingDimensionsData.length > 0 ? (
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <MetricTargetBars
                            data={wellbeingDimensionsData}
                            seriesKeys={['currentScore']}
                            seriesLabels={['Current Score']}
                            metricKey='metric'
                            height={250}
                            colors={[theme.palette.primary.main]}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Stack spacing={2}>
                            {wellbeingDimensionsData.map((item, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 1.5,
                                }}
                              >
                                <AdsClickIcon
                                  sx={{
                                    fontSize: 20,
                                    color: theme.palette.text.secondary,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant='body2' color='text.secondary' sx={{ width: '40%', flexShrink: 0 }}>
                                  {item.metric}
                                </Typography>
                                <LinearProgress
                                  variant='determinate'
                                  value={item.currentScore}
                                  sx={{
                                    height: 15,
                                    width: '100%',
                                    borderRadius: 1,
                                    bgcolor: theme.palette.action.hover,
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 1,
                                      bgcolor: theme.palette.info.light,
                                    },
                                  }}
                                />
                                <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
                                  {formatPercentValue(item.currentScore, 2, true)}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Grid>
                      </Grid>
                    ) : (
                      <Alert severity='info'>No wellbeing dimensions data available</Alert>
                    )}
                  </CohortGuard>
                </DashboardCard>
              </Grid>
            </Grid>

            {(selectedBucket === 'all' || selectedBucket === 'biometrics') &&
              availableBuckets.some((b) => b.key === 'biometrics') && (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <DashboardCard
                      title='Biometric Trends'
                      subtitle='Daily variations across core vital metrics.'
                      metricKey={toMetricKey(biometricTrendView)}
                      titleComponent={
                        <ToggleButtonGroup
                          value={biometricTrendView}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) setBiometricTrendView(newValue);
                          }}
                          size='small'
                        >
                          <ToggleButton value='heart-rate'>Heart Rate</ToggleButton>
                          <ToggleButton value='heart-rate-variability'>Heart Rate Variability</ToggleButton>
                          <ToggleButton value='resting-hr'>Resting HR</ToggleButton>
                          <ToggleButton value='respiratory-rate'>Respiratory Rate</ToggleButton>
                          <ToggleButton value='spo2'>Blood Oxygen (SpO2)</ToggleButton>
                        </ToggleButtonGroup>
                      }
                    >
                      <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                        {isLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 300,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : trendsData.length > 0 ? (
                          renderBiometricTrendChart()
                        ) : (
                          <Alert severity='info'>No trend data available</Alert>
                        )}
                      </CohortGuard>
                    </DashboardCard>
                  </Grid>
                </Grid>
              )}

            {(selectedBucket === 'all' || selectedBucket === 'sleep') &&
              availableBuckets.some((b) => b.key === 'sleep') && (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <DashboardCard
                      title='Sleep Trends'
                      subtitle='Daily patterns of sleep stages showing fluctuations in recovery and rest quality.'
                      metricKey={toMetricKey(sleepTrendView)}
                      titleComponent={
                        <ToggleButtonGroup
                          value={sleepTrendView}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) setSleepTrendView(newValue);
                          }}
                          size='small'
                        >
                          <ToggleButton value='sleep-stages'>Sleep Stages</ToggleButton>
                          <ToggleButton value='total-sleep'>Total Sleep</ToggleButton>
                          <ToggleButton value='efficiency'>Efficiency</ToggleButton>
                          <ToggleButton value='sleep-debt'>Sleep Debt</ToggleButton>
                          <ToggleButton value='sleep-stress'>Sleep Stress</ToggleButton>
                        </ToggleButtonGroup>
                      }
                    >
                      <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                        {isLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 300,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : trendsData.length > 0 ? (
                          renderSleepTrendChart()
                        ) : (
                          <Alert severity='info'>No trend data available</Alert>
                        )}
                      </CohortGuard>
                    </DashboardCard>
                  </Grid>
                </Grid>
              )}

            {(selectedBucket === 'all' || selectedBucket === 'recovery') &&
              availableBuckets.some((b) => b.key === 'recovery') && (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <DashboardCard
                      title='Recovery Trends'
                      subtitle='Daily trends showing recovery and strain percentages over time.'
                      metricKey={toMetricKey(recoveryTrendView)}
                      titleComponent={
                        <ToggleButtonGroup
                          value={recoveryTrendView}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) setRecoveryTrendView(newValue);
                          }}
                          size='small'
                        >
                          <ToggleButton value='recovery-score'>Recovery Score</ToggleButton>
                          <ToggleButton value='resting-heart-rate'>Resting Heart Rate</ToggleButton>
                        </ToggleButtonGroup>
                      }
                    >
                      <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                        {isLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 300,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : trendsData.length > 0 ? (
                          renderRecoveryTrendChart()
                        ) : (
                          <Alert severity='info'>No trend data available</Alert>
                        )}
                      </CohortGuard>
                    </DashboardCard>
                  </Grid>
                </Grid>
              )}

            {(selectedBucket === 'all' || selectedBucket === 'strain') &&
              availableBuckets.some((b) => b.key === 'strain') && (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <DashboardCard
                      title='Strain Trends'
                      subtitle='Daily strain and calorie expenditure over time.'
                      metricKey={toMetricKey(strainTrendView)}
                      titleComponent={
                        <ToggleButtonGroup
                          value={strainTrendView}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) setStrainTrendView(newValue);
                          }}
                          size='small'
                        >
                          <ToggleButton value='strain'>Strain</ToggleButton>
                          <ToggleButton value='calories'>Calorie Expenditure</ToggleButton>
                        </ToggleButtonGroup>
                      }
                    >
                      <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                        {isLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 300,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : trendsData.length > 0 ? (
                          renderStrainTrendChart()
                        ) : (
                          <Alert severity='info'>No trend data available</Alert>
                        )}
                      </CohortGuard>
                    </DashboardCard>
                  </Grid>
                </Grid>
              )}
          </Stack>
        )}

        {viewMode === 'ai-powered-wellness' && isAiEnabled && (
          <Stack spacing={3}>
            {standardInsightsError && (
              <Alert severity='error'>
                Failed to load insights.{' '}
                {standardInsightsError instanceof Error ? standardInsightsError.message : 'Please try again.'}
              </Alert>
            )}

            {standardInsightsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            )}

            {standardInsights && (
              <AiWellnessPanel
                insights={standardInsights}
                scopeKey={insightsDepartmentId ? `team:${insightsDepartmentId}` : 'team:all'}
                scopeLabel={insightsDepartmentId ? 'Selected team' : 'All teams'}
                initialPrompt='What stands out this week and what should we do next?'
                buildRequest={(question) => ({
                  scope: insightsDepartmentId ? { type: 'department', id: insightsDepartmentId } : { type: 'org' },
                  question,
                  start_date: standardInsightsParams.start_date,
                  end_date: standardInsightsParams.end_date,
                  client_id: standardInsightsParams.client_id,
                  k: 8,
                })}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
